"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/effect-fade"
import { Autoplay, EffectFade } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { LeftArrow2Icon, StartIcon, StopSign, transitionTime, useIsDesktop } from "ui"
import {
  extractProperty,
  getLinkHrefFromMultiUrlPickerItem,
  getUmbracoMediaUrl,
  replaceRichTextLinks,
} from "utils"
import { useTypingAnimation } from "../hooks/useTypingAnimation"
import { useAnchorScroll } from "../useAnchorScroll"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
  locale?: string
}

// --- Styled Components (mobile-first, breakpoints.up only) ---

const BP_DESKTOP = "@media (min-width: 1024px)"

const Section = styled.section`
  display: flex;
  flex-direction: column;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  position: relative;
`

const SliderArea = styled.div`
  position: relative;
  width: 100%;
  height: 404px;

  ${BP_DESKTOP} {
    height: 525px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    height: 700px;
  }

  .swiper {
    width: 100%;
    height: 100%;
    padding-block: 0;
  }

  .swiper-slide {
    position: relative;
    width: 100%;
    height: 100%;
    opacity: 0 !important;
    pointer-events: none;
  }

  .swiper-slide-active {
    opacity: 1 !important;
    pointer-events: auto;
    z-index: 2;
  }
`

const EdgeArrow = styled.button<{ side: "left" | "right" }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background-color: var(--comp-bg);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 58px;
  padding: 0;

  ${({ side }) =>
    side === "left"
      ? "left: 0; border-radius: 0 50px 50px 0;"
      : "right: 0; border-radius: 50px 0 0 50px;"}

  svg {
    width: 28px;
    height: 28px;
  }

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: -2px;
  }

  ${BP_DESKTOP} {
    width: 81px;
    height: 98px;

    svg {
      width: 50px;
      height: 50px;
    }
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    width: 108px;
    height: 130px;
    svg {
      width: 66px;
      height: 66px;
    }
    ${({ side }) =>
      side === "left"
        ? "left: 0; border-radius: 0 66px 66px 0;"
        : "right: 0; border-radius: 66px 0 0 66px;"}
  }
`

const PlayPauseBtn = styled.button`
  position: absolute;
  bottom: 20px;
  right: 16px;
  z-index: 10;
  background: rgba(255, 255, 255, 1);
  opacity: 0.4;
  border: none;
  border-radius: 40px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  ${BP_DESKTOP} {
    width: 50px;
    height: 50px;
    bottom: auto;
    right: 81px;
    top: calc(525px - 87px);
    svg {
      width: 30px;
      height: 30px;
    }
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    width: 66px;
    height: 66px;
    svg {
      width: 40px;
      height: 40px;
    }
    top: calc(700px - 87px);
  }
`

const VideoPlayPauseBtn = styled.button`
  position: absolute;
  top: 20px;
  inset-inline-end: 20px;
  z-index: 10;
  background: rgba(255, 255, 255, 1);
  opacity: 0.4;
  border: none;
  border-radius: 40px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  ${BP_DESKTOP} {
    width: 67px;
    height: 67px;

    svg {
      width: 30px;
      height: 30px;
    }
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    svg {
      width: 40px;
      height: 40px;
    }
  }
`

const ContentBarContainer = styled.div`
  min-height: 296px;
  padding: 24px 20px 40px;
  overflow: visible;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: 1;
  ${BP_DESKTOP} {
    min-height: 375px;
    display: flex;
    height: 100%;
    padding: 40px 120px 64px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    height: auto;
    min-height: 287px;
    padding: 40px 240px 64px;
  }
`

const ContentBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  flex-grow: 0;
  flex-basis: calc(100% + 32px);
  ${BP_DESKTOP} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    min-height: unset;
  }
`

const LogoArea = styled.div`
  order: -1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  gap: 8px;
  flex-wrap: wrap;
  overflow: hidden;

  img {
    max-width: 157px;
    max-height: 97px;
  }

  ${BP_DESKTOP} {
    order: 0;
    flex-shrink: 0;
    width: max-content;
    padding: 0;
    align-self: flex-start;
    margin-block: 15px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    margin-block: 20px;
    img {
      max-width: 209px;
      max-height: 129px;
    }
  }
`

const TextAndButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  flex: 1;

  ${BP_DESKTOP} {
    min-width: 0;
    align-items: flex-start;
    gap: 24px;
  }
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  width: 100%;
  flex: 1;

  ${BP_DESKTOP} {
    text-align: left;
    flex: unset;
  }
`

const Headline = styled.h1`
  color: var(--comp-heading);
  margin: 0;
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.8px;
  position: relative;

  ${BP_DESKTOP} {
    font-size: 72px;
  }
`

const HeadlinePlaceholder = styled.span`
  display: block;
  visibility: hidden;
`

const HeadlineTyped = styled.span`
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 1;
`

const BodyText = styled.div`
  color: var(--comp-text);

  font-size: 18px;
  font-weight: 700;
  line-height: 150%; /* 27px */
  flex: 1;
  p {
    margin: 0;
  }

  ${BP_DESKTOP} {
    flex: unset;
    font-size: 24px;
    line-height: 130%; /* 31.2px */
    letter-spacing: -0.48px;
  }
`

const ButtonsRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;

  ${BP_DESKTOP} {
    width: max-content;
  }
`

const CtaButton = styled(Button)`
  && {
    flex: 1;
    height: 40px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 700;
    text-transform: none;
    background-color: var(--inner-bg);
    color: var(--inner-text);

    &:hover {
      background-color: var(--inner-bg);
      opacity: 0.9;
    }

    ${BP_DESKTOP} {
      height: 48px;
      font-size: 16px;
    }

    ${(props) => props.theme.breakpoints.up("xl")} {
      font-size: 20px;
    }
  }
`

const SecondaryButton = styled(Button)`
  && {
    flex: 1;
    height: 40px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 700;
    text-transform: none;
    background-color: transparent;
    color: var(--inner-bg);
    border: 2px solid var(--inner-bg);

    &:hover {
      background-color: transparent;
      opacity: 0.9;
    }

    ${BP_DESKTOP} {
      height: 48px;
      font-size: 16px;
    }

    ${(props) => props.theme.breakpoints.up("xl")} {
      font-size: 20px;
    }
  }
`

// --- Sub-components ---

function SingleMedia({ item }: { item: any }) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const url = item?.url || ""
  const isVideo = url.endsWith(".mp4") || url.endsWith(".webm")
  const videoRef = useRef<HTMLVideoElement>(null)
  const [paused, setPaused] = useState(false)

  if (isVideo) {
    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <video
          ref={videoRef}
          src={getUmbracoMediaUrl(url)}
          autoPlay
          muted
          loop
          playsInline
          onPlay={() => setPaused(false)}
          onPause={() => setPaused(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <VideoPlayPauseBtn
          onClick={() => {
            if (videoRef.current) {
              analytics.mediaClick(paused ? "play" : "pause", LANDING_ELEMENTS.hero)
              if (paused) videoRef.current.play()
              else videoRef.current.pause()
            }
          }}
          aria-label={
            paused ? (isRtl ? "הפעל וידאו" : "Play video") : isRtl ? "עצור וידאו" : "Pause video"
          }
        >
          {paused ? <StartIcon /> : <StopSign />}
        </VideoPlayPauseBtn>
      </div>
    )
  }

  return (
    <Image
      src={getUmbracoMediaUrl(url)}
      fill
      alt={item?.altText || ""}
      style={{ objectFit: "cover" }}
    />
  )
}

// --- Main Component ---

function HeroOption2({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const logos = extractProperty(data, "heroLogos", true) as any[] | undefined
  const headline = extractProperty(data, "heroHeadline") as string | undefined
  const bodyText = extractProperty(data, "heroBodyText") as string | undefined
  const ctaButton = extractProperty(data, "heroCtaButton") as any[] | undefined
  const secondaryBtn = extractProperty(data, "heroSecondaryButton") as any[] | undefined
  const desktopMedia = extractProperty(data, "heroBackgroundMedia", true) as any[] | undefined
  const mobileMedia = extractProperty(data, "heroBackgroundMediaMobile", true) as any[] | undefined
  const isDesktop = useIsDesktop()
  const mediaItems = isDesktop ? desktopMedia : mobileMedia?.length ? mobileMedia : desktopMedia

  const isRtl = locale === "he" || !locale
  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const { ref: headlineRef, inView: headlineInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  })
  const { displayed: typedHeadline, full: fullHeadline } = useTypingAnimation(
    headline,
    headlineInView,
  )

  const hasMedia = Array.isArray(mediaItems) && mediaItems.length > 0
  const hasMultipleMedia = hasMedia && mediaItems!.length > 1
  const showArrows = hasMultipleMedia
  const showPlayPause = hasMultipleMedia

  const togglePlayPause = useCallback(() => {
    if (swiperRef) {
      if (isPlaying) {
        swiperRef.autoplay.stop()
      } else {
        swiperRef.autoplay.start()
      }
    }
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.hero)
    setIsPlaying(!isPlaying)
  }, [swiperRef, isPlaying, analytics])

  useEffect(() => {
    if (!containerRef.current || !hasMultipleMedia) return
    const slides = containerRef.current.querySelectorAll(".swiper-slide")
    slides.forEach((slide, i) => {
      if (i === activeIndex) {
        slide.removeAttribute("inert")
        slide.removeAttribute("aria-hidden")
      } else {
        slide.setAttribute("inert", "")
        slide.setAttribute("aria-hidden", "true")
      }
    })
  }, [activeIndex, hasMultipleMedia])

  const ctaLink = ctaButton?.[0]
  const secondaryLink = secondaryBtn?.[0]
  const ctaHref = ctaLink ? getLinkHrefFromMultiUrlPickerItem(ctaLink as any) : undefined
  const secondaryHref = secondaryLink
    ? getLinkHrefFromMultiUrlPickerItem(secondaryLink as any)
    : undefined
  const handleAnchorClick = useAnchorScroll()
  const logoItems = Array.isArray(logos) ? logos : []
  const hasSingleButton = (ctaLink && !secondaryLink) || (!ctaLink && secondaryLink)

  return (
    <Section>
      {/* Full-width slider area */}
      {hasMedia && (
        <SliderArea ref={containerRef}>
          {mediaItems!.length === 1 ? (
            <SingleMedia item={mediaItems![0]} />
          ) : (
            <Swiper
              loop
              onSwiper={setSwiperRef}
              onSlideChange={(swiper) => {
                setActiveIndex(swiper.realIndex)
                const activeSlide = swiper.slides[swiper.activeIndex]
                const video = activeSlide?.querySelector("video")
                if (video) {
                  video.currentTime = 0
                  video.play()
                }
              }}
              modules={[EffectFade, Autoplay]}
              effect="fade"
              speed={transitionTime * 1000}
              style={{ width: "100%", height: "100%" }}
              autoplay={
                isPlaying
                  ? {
                      delay: 4500,
                      disableOnInteraction: false,
                    }
                  : false
              }
            >
              {mediaItems!.map((item: any, i: number) => (
                <SwiperSlide key={i}>
                  <SingleMedia item={item} />
                </SwiperSlide>
              ))}
            </Swiper>
          )}

          {/* No gradient overlay on Hero Option 2 */}

          {/* Edge arrows */}
          {showArrows && (
            <>
              <EdgeArrow
                side="left"
                onClick={() => swiperRef?.slidePrev()}
                aria-label={isRtl ? "לתמונה הקודמת" : "Previous slide"}
              >
                {
                  <LeftArrow2Icon
                    color="white"
                    style={isRtl ? {} : { transform: "rotate(180deg)" }}
                  />
                }
              </EdgeArrow>
              <EdgeArrow
                side="right"
                onClick={() => swiperRef?.slideNext()}
                aria-label={isRtl ? "לתמונה הבאה" : "Next slide"}
              >
                {
                  <LeftArrow2Icon
                    color="white"
                    style={isRtl ? { transform: "rotate(180deg)" } : {}}
                  />
                }
              </EdgeArrow>
            </>
          )}

          {/* Slide autoplay Play/Pause */}
          {showPlayPause && (
            <PlayPauseBtn
              onClick={togglePlayPause}
              aria-label={
                isPlaying
                  ? isRtl
                    ? "עצור תנועת הרכיב"
                    : "Stop animation"
                  : isRtl
                  ? "הפעל תנועה רכיב"
                  : "Play animation"
              }
            >
              {isPlaying ? <StopSign fill="#000" /> : <StartIcon fill="#000" />}
            </PlayPauseBtn>
          )}
        </SliderArea>
      )}

      {/* Content bar below slider */}
      <ContentBarContainer>
        <ContentBar>
          {/* Text + buttons — first in JSX = right in RTL */}
          <TextAndButtons>
            <TextBlock>
              {headline && (
                <Headline ref={headlineRef}>
                  <HeadlinePlaceholder>{fullHeadline}</HeadlinePlaceholder>
                  <HeadlineTyped>{typedHeadline}</HeadlineTyped>
                </Headline>
              )}
              {bodyText && (
                <BodyText
                  dangerouslySetInnerHTML={{
                    __html: replaceRichTextLinks(bodyText, true),
                  }}
                />
              )}
            </TextBlock>

            {(ctaLink || secondaryLink) && (
              <ButtonsRow style={hasSingleButton ? { justifyContent: "center" } : undefined}>
                {ctaLink && (
                  <CtaButton
                    variant="contained"
                    href={ctaHref}
                    onClick={(e) => {
                      analytics.ctaClick(ctaLink.name, LANDING_ELEMENTS.hero)
                      handleAnchorClick(e, ctaHref)
                    }}
                    disableElevation
                  >
                    {ctaLink.name}
                  </CtaButton>
                )}
                {secondaryLink && (
                  <SecondaryButton
                    variant="outlined"
                    href={secondaryHref}
                    onClick={(e) => {
                      analytics.ctaClick(secondaryLink.name, LANDING_ELEMENTS.hero)
                      handleAnchorClick(e, secondaryHref)
                    }}
                  >
                    {secondaryLink.name}
                  </SecondaryButton>
                )}
              </ButtonsRow>
            )}
          </TextAndButtons>

          {/* Logo — second in JSX = left in RTL */}
          {logoItems.length > 0 && (
            <LogoArea>
              {logoItems.map((logo: any, i: number) => (
                <Image
                  key={i}
                  src={getUmbracoMediaUrl(logo?.url || "/")}
                  width={209}
                  height={129}
                  alt={logo?.altText || ""}
                  style={{ objectFit: "contain" }}
                />
              ))}
            </LogoArea>
          )}
        </ContentBar>
      </ContentBarContainer>
    </Section>
  )
}

export default HeroOption2
